import React, {useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Link} from "react-router";
import {Eye, Loader2, Pencil, PlusCircle, Search} from "lucide-react";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface AdminUserListItem {
  id: string;
  email: string;
  name: string | null;
  role: "USER" | "ADMIN";
  createdAt: string | null;
  updatedAt: string | null;
  orgCount: number;
  recordCount: number;
  lastLoginAt: string | null;
  lastActivityAt: string | null;
}

type SortKey =
  | "name"
  | "createdAt"
  | "lastLoginAt"
  | "lastActivityAt"
  | "recordCount";
type RoleFilter = "ALL" | "ADMIN" | "USER";

const formatDateTime = (value: string | null) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const sortUsers = (users: AdminUserListItem[], sortKey: SortKey) => {
  return [...users].sort((a, b) => {
    if (sortKey === "name") {
      return (a.name ?? a.email).localeCompare(b.name ?? b.email);
    }

    if (sortKey === "recordCount") {
      return b.recordCount - a.recordCount;
    }

    const aTime = a[sortKey] ? new Date(a[sortKey]).getTime() : 0;
    const bTime = b[sortKey] ? new Date(b[sortKey]).getTime() : 0;
    return bTime - aTime;
  });
};

const AdminUsers: React.FC = () => {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("ALL");
  const [sortKey, setSortKey] = useState<SortKey>("lastLoginAt");

  const {data: users, isLoading, error} = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await fetch("/api/user");
      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }
      return response.json() as Promise<AdminUserListItem[]>;
    },
  });

  const filteredUsers = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    const matches = (users ?? []).filter((user) => {
      const matchesSearch =
        !searchTerm ||
        user.email.toLowerCase().includes(searchTerm) ||
        (user.name ?? "").toLowerCase().includes(searchTerm);
      const matchesRole = roleFilter === "ALL" || user.role === roleFilter;
      return matchesSearch && matchesRole;
    });

    return sortUsers(matches, sortKey);
  }, [users, search, roleFilter, sortKey]);

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Users</h1>
          <p className="text-muted-foreground">Platform user directory.</p>
        </div>
        <Button asChild>
          <Link to="/app/admin/users/create">
            <PlusCircle className="mr-2 h-4 w-4" />
            Add User
          </Link>
        </Button>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-[1fr_160px_200px]">
        <div className="relative">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-9"
            placeholder="Search name or email"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(event) => setRoleFilter(event.target.value as RoleFilter)}
          className="border-input bg-background rounded-md border px-3 py-2 text-sm"
          aria-label="Filter by role"
        >
          <option value="ALL">All roles</option>
          <option value="ADMIN">Admins</option>
          <option value="USER">Users</option>
        </select>
        <select
          value={sortKey}
          onChange={(event) => setSortKey(event.target.value as SortKey)}
          className="border-input bg-background rounded-md border px-3 py-2 text-sm"
          aria-label="Sort users"
        >
          <option value="lastLoginAt">Last login</option>
          <option value="lastActivityAt">Last activity</option>
          <option value="recordCount">Record count</option>
          <option value="createdAt">Created date</option>
          <option value="name">Name</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-4">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 p-4 text-red-800">
          Failed to load users. Please try again.
        </div>
      ) : filteredUsers.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Organisations</TableHead>
              <TableHead>Records</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead>Last activity</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="font-medium">{user.name || "Unnamed user"}</div>
                  <div className="text-muted-foreground text-xs">{user.email}</div>
                </TableCell>
                <TableCell>
                  <Badge variant={user.role === "ADMIN" ? "default" : "secondary"}>
                    {user.role === "ADMIN" ? "System Admin" : "User"}
                  </Badge>
                </TableCell>
                <TableCell>{user.orgCount}</TableCell>
                <TableCell>{user.recordCount}</TableCell>
                <TableCell>{formatDateTime(user.lastLoginAt)}</TableCell>
                <TableCell>{formatDateTime(user.lastActivityAt)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/app/admin/user/${user.id}`}>
                        <Eye className="mr-1 h-4 w-4" />
                        View
                      </Link>
                    </Button>
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/app/admin/user/${user.id}/edit`}>
                        <Pencil className="mr-1 h-4 w-4" />
                        Edit
                      </Link>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="py-8 text-center text-muted-foreground">No users found</div>
      )}
    </div>
  );
};

export {AdminUsers};
